import {
  AfterViewChecked,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  QueryList,
  ViewChildren,
} from '@angular/core';

import { OrbiAccession } from '@patient-ui/shared/models';

/** The two navigation intents a Results card can emit (PCSA-13224). */
export interface OrbiResultsCardNavigate {
  /** `view-details` targets one accession; `view-all` opens the Results list. */
  kind: 'view-details' | 'view-all';
  /** The accession to open (present only for `view-details`). */
  accession?: OrbiAccession;
}

/**
 * Renders a PatientResults structured card in the Orbi chat thread (PCSA-13224).
 *
 * Behavior + data mirror the mobile app (`orbi_results_card.dart` /
 * `NewResultCardContent`); the LAYOUT follows the web Figma ("AI Agent Canvas"),
 * whose two frames are structurally different cards rather than one card with a
 * repeat:
 *
 *  - **Single Accession** — one card headed by the vial glyph + "Results from
 *    MM/DD/YYYY", the provider line in the heading weight beneath it, a bulleted
 *    test list, then "View Details" / "View All Results" (+ "Show N More" once
 *    the list is truncated).
 *  - **Multi Accession** — one card headed by the same glyph + a bare "Results",
 *    owning a stack of BORDERED INNER cards. Each inner card is a labeled
 *    summary — "Ordered Tests:", "Order From:", "Purchase Date:" — with a status
 *    pill and its own "View ›" link, and the outer card carries the shared
 *    "Show N More" + "View All Results". This is the same outer-card/inner-card
 *    construct the Visit History card uses, and it is why the multi case does not
 *    just repeat the single card.
 *
 * Both frames also come in a WIDE and a NARROW variant, which differ only in
 * whether the footer controls sit inline or stack, and (single) whether the test
 * list is one or two columns — so they are breakpoints of one card, not states.
 *
 * "Show N More" counts ACCESSIONS in the multi frame and TESTS in the single one:
 * each frame truncates the list it actually owns.
 *
 * The Unread/Updated badge is suppressed by default on the single frame (mobile
 * passes `showBadge: false` and the agent payload carries no read status), but
 * the multi frame's inner cards always draw their status pill — the Figma shows
 * one on every row, and it is the only per-row state that frame conveys.
 *
 * Presentational only — navigation + panel-close are the host thread's concern;
 * this component just emits `navigate`.
 */
@Component({
  selector: 'patient-ui-orbi-results-card',
  templateUrl: './orbi-results-card.component.html',
  styleUrls: ['./orbi-results-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrbiResultsCardComponent implements AfterViewChecked {
  /** The accessions to render (already parsed by the response parser). */
  @Input() accessions: OrbiAccession[] = [];

  /**
   * Show the Unread/Updated badge. Off by default for parity with the mobile
   * Orbi cards; kept as an input so a status-bearing accession can opt in.
   */
  @Input() showBadge = false;

  /** Emitted for "View Details"/"View" and "View All Results". */
  @Output() navigate = new EventEmitter<OrbiResultsCardNavigate>();

  @ViewChildren('accessionCard') private accessionCards?: QueryList<
    ElementRef<HTMLElement>
  >;

  /** Tests shown before "Show More" expands the list (mirrors mobile's 8). */
  private static readonly MAX_VISIBLE_TESTS = 8;

  /**
   * Inner cards shown before the multi frame's "Show N More" expands the stack.
   * 3 is what the Figma frame draws (two rows visible above "Show 3 More" out of
   * five), and it keeps the collapsed card short enough to read inside the panel.
   */
  private static readonly MAX_VISIBLE_ACCESSIONS = 3;

  /** Group heading on the multi frame (the single frame dates its own title). */
  readonly groupTitle = 'Results';

  /** Per-accession expanded (Show More) state, keyed by accession id. */
  private readonly expanded = new Set<number>();

  /** Whether the multi frame's accession stack is expanded past the first N. */
  private accessionsExpanded = false;

  private pendingRevealFocus: number | null = null;

  ngAfterViewChecked(): void {
    const index = this.pendingRevealFocus;
    if (index === null) {
      return;
    }
    this.pendingRevealFocus = null;
    this.focusRevealedCard(index);
  }

  /** True when exactly one accession is present (single-card layout). */
  get isSingle(): boolean {
    return this.accessions.length === 1;
  }

  /** The lone accession in the single-card layout (guarded by `isSingle`). */
  get single(): OrbiAccession {
    return this.accessions[0];
  }

  // --- Multi frame: the accession stack --------------------------------------

  /** The inner cards to render now — all when expanded, else the first N. */
  get visibleAccessions(): OrbiAccession[] {
    if (this.accessionsExpanded) {
      return this.accessions;
    }
    return this.accessions.slice(
      0,
      OrbiResultsCardComponent.MAX_VISIBLE_ACCESSIONS
    );
  }

  /** True when the set holds more accessions than the collapsed stack shows. */
  get hasMoreAccessions(): boolean {
    return (
      this.accessions.length > OrbiResultsCardComponent.MAX_VISIBLE_ACCESSIONS
    );
  }

  /** Whether the accession stack is currently expanded. */
  get isAccessionsExpanded(): boolean {
    return this.accessionsExpanded;
  }

  /**
   * The multi frame's toggle label. Figma counts the REMAINDER ("Show 3 More"),
   * not the total, so the control states exactly what pressing it adds.
   */
  get accessionsToggleLabel(): string {
    if (this.accessionsExpanded) {
      return 'Show Less';
    }
    const hidden =
      this.accessions.length - OrbiResultsCardComponent.MAX_VISIBLE_ACCESSIONS;
    return `Show ${hidden} More`;
  }

  toggleAccessionsExpanded(): void {
    const firstRevealed = this.accessionsExpanded
      ? null
      : OrbiResultsCardComponent.MAX_VISIBLE_ACCESSIONS;
    this.accessionsExpanded = !this.accessionsExpanded;
    this.pendingRevealFocus = firstRevealed;
  }

  private focusRevealedCard(fromIndex: number): void {
    const cards = this.accessionCards?.toArray() ?? [];
    for (let i = fromIndex; i < cards.length; i++) {
      const link = cards[i].nativeElement.querySelector<HTMLElement>(
        '.orbi-results-card__view .btn'
      );
      if (link) {
        link.focus();
        return;
      }
    }
  }

  // --- Inner-card summary rows (multi frame) ---------------------------------

  /**
   * "Ordered Tests:" value for an inner card. The Figma frame renders this two
   * ways from the same field — a single test inline after the label, several as a
   * bulleted list under it — so the template picks by count and this returns the
   * names either way.
   */
  orderedTests(accession: OrbiAccession): string[] {
    return this.testNames(accession);
  }

  /** True when an inner card carries several tests, which the two frames draw
   * differently (a bullet-separated run wide, a bulleted list narrow). */
  hasTestList(accession: OrbiAccession): boolean {
    return this.testNames(accession).length > 1;
  }

  /**
   * The wide multi frame's inline form of the same test names — one run separated
   * by bullets ("Iron • Ferritin • Hemoglobin"), which is what that frame draws
   * instead of the narrow frame's bulleted list. The separator is a real U+2022
   * with spaces either side so the run wraps BETWEEN names rather than mid-name,
   * matching how the frame breaks its lines.
   */
  testRun(accession: OrbiAccession): string {
    return this.testNames(accession).join(' • ');
  }

  /**
   * "Order From:" value — the same provider/account join the single frame's
   * provider line uses, so one accession reads identically in both frames.
   */
  orderFrom(accession: OrbiAccession): string {
    return this.providerLine(accession);
  }

  /**
   * "Purchase Date:" value. Figma labels this row "Purchase Date", which is the
   * portal's own wording for the ORDERED date (`portal-results-detail` renders
   * `Purchase Date: {{ header.orderedDate }}`), so it reads `orderedDate` rather
   * than the date of service in the title. Empty when absent, so the row hides.
   */
  purchaseDate(accession: OrbiAccession): string {
    return this.formatDate(accession.orderedDate);
  }

  /**
   * Status pill on an inner card: "Updated" when the accession was revised, else
   * "Complete". Unlike the single frame's optional badge this is always drawn —
   * the Figma frame shows a pill on every row, and "Complete" is the resting
   * state a returned result is in (the payload only ever reports a REPORTED
   * accession, so there is no in-progress case to distinguish).
   */
  accessionStatus(accession: OrbiAccession): 'Updated' | 'Complete' {
    return accession.isUpdated ? 'Updated' : 'Complete';
  }

  /**
   * Per-card title (single or one card in a multi set): "Results from
   * MM/DD/YYYY" when the accession has a valid date of service (Figma), else
   * just "Results" — never a dangling "Results from ". Multi sets also render a
   * "Results" group header above the cards (see the template).
   */
  cardTitle(accession: OrbiAccession | undefined): string {
    const date = this.formatDate(accession?.dateOfService);
    return date ? `Results from ${date}` : 'Results';
  }

  /**
   * Provider display line: "provider, account" (mobile joins them, dropping any
   * empty part). Empty when neither is present, so the row is hidden.
   */
  providerLine(accession: OrbiAccession): string {
    return [accession.orderingProviderName, accession.accountName]
      .filter((part): part is string => !!part && part.trim().length > 0)
      .join(', ');
  }

  /** All ordered-test names on an accession, in payload order. */
  testNames(accession: OrbiAccession): string[] {
    return (accession.testCodes ?? []).map((tc) => tc.testName);
  }

  /** The tests to render now — all when expanded, else the first N. */
  visibleTests(accession: OrbiAccession): string[] {
    const names = this.testNames(accession);
    if (this.isExpanded(accession)) {
      return names;
    }
    return names.slice(0, OrbiResultsCardComponent.MAX_VISIBLE_TESTS);
  }

  /** True when the accession has more tests than fit before "Show More". */
  hasMore(accession: OrbiAccession): boolean {
    return (
      this.testNames(accession).length >
      OrbiResultsCardComponent.MAX_VISIBLE_TESTS
    );
  }

  testsToggleLabel(accession: OrbiAccession): string {
    return this.isExpanded(accession) ? 'Show Less' : 'Show More';
  }

  /** Whether this accession's test list is currently expanded. */
  isExpanded(accession: OrbiAccession): boolean {
    return this.expanded.has(accession.id);
  }

  /** Toggle the Show More/Less state for one accession. */
  toggleExpanded(accession: OrbiAccession): void {
    if (this.expanded.has(accession.id)) {
      this.expanded.delete(accession.id);
    } else {
      this.expanded.add(accession.id);
    }
  }

  /**
   * Badge for an accession (only when `showBadge`): "Updated" when `isUpdated`,
   * else "Unread" (mirrors mobile `AccessionDateRow`). Empty when the badge is
   * off or no status applies, so the pill is hidden.
   */
  badgeLabel(accession: OrbiAccession): '' | 'Updated' | 'Unread' {
    if (!this.showBadge) {
      return '';
    }
    if (accession.isUpdated) {
      return 'Updated';
    }
    if (accession.isNew) {
      return 'Unread';
    }
    return '';
  }

  onViewDetails(accession: OrbiAccession): void {
    this.navigate.emit({ kind: 'view-details', accession });
  }

  onViewAll(): void {
    this.navigate.emit({ kind: 'view-all' });
  }

  /** Track accessions by their stable id for *ngFor. */
  trackById(_index: number, accession: OrbiAccession): number {
    return accession.id;
  }

  /**
   * ISO date -> MM/DD/YYYY, formatted in UTC (an ISO instant like
   * `2026-06-08T00:00:00Z` must render as that calendar date in every timezone —
   * same reasoning as the history-item date label). Empty for missing/invalid.
   */
  private formatDate(raw: string | undefined): string {
    const d = raw ? new Date(raw) : null;
    if (!d || isNaN(d.getTime())) {
      return '';
    }
    const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(d.getUTCDate()).padStart(2, '0');
    return `${mm}/${dd}/${d.getUTCFullYear()}`;
  }
}
